import {
  useCreateDispatchTicketMutation,
  useCreateRecurringDispatchTicketMutation,
  useGetDispatchTicketQuery,
  useUpdateDispatchTicketMutation,
} from 'services/dispatch'
import {
  formatSubmitAttachments,
  formatSubmitBladeData,
  formatSubmitData,
} from 'components/containers/load-board/utils/formatFunctions'
import { SUBMIT_FORM_TYPES } from 'components/containers/load-board/utils/constants'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore.ts'
import {
  CreateDispatchTicketApiArg,
  CreateRecurringDispatchTicketApiArg,
  UpdateDispatchTicketApiArg,
} from 'gen/rtk-openapi.ts'

interface DispatchTicketSubmitConfig {
  status?: string
  handleSuccess: (data: any, updatedDT: any) => void
  handleError: (error: any) => void
  idempotencyKey?: string
  submitFormType?: SUBMIT_FORM_TYPES
}

export const useDispatchTicket = ({ dispatchTicketId, methods }) => {
  const { inOrgMode } = useAppSelector(state => state.ui)
  const { yardDetailsList } = useAppSelector(state => state.auth)

  const isUpdate = !!dispatchTicketId
  const { getValues } = methods
  const { data: dispatchTicketData, isLoading: isLoadingDispatchTicketData } = useGetDispatchTicketQuery(
    { dispatchTicketId },
    { skip: !isUpdate || !yardDetailsList },
  )

  const [createDispatchTicket, { isLoading: isLoadingCreateDispatchTicket }] = useCreateDispatchTicketMutation()

  const [updateDispatchTicket, { isLoading: isLoadingUpdateDispatchTicket }] = useUpdateDispatchTicketMutation()

  const [createRecurringDispatchTicket, { isLoading: isLoadingRecurringDispatchTicket }] =
    useCreateRecurringDispatchTicketMutation()

  const isLoadingDispatchTicket =
    isLoadingDispatchTicketData || isLoadingCreateDispatchTicket || isLoadingUpdateDispatchTicket

  const onSubmitDispatchTicket = async (dataParams: any, config: DispatchTicketSubmitConfig) => {
    const { status, handleSuccess, handleError, idempotencyKey, submitFormType = SUBMIT_FORM_TYPES.DEFAULT } = config
    const data = {
      ...dispatchTicketData,
      ...(dataParams || getValues()),
      ...(status && { status }),
      ...(idempotencyKey && { idempotencyKey }),
      hash: dispatchTicketData?.hash,
    }

    let formatFunction
    switch (submitFormType) {
      case SUBMIT_FORM_TYPES.BLADE:
        formatFunction = formatSubmitBladeData
        break
      case SUBMIT_FORM_TYPES.ATTACHMENTS:
        formatFunction = formatSubmitAttachments
        break
      case SUBMIT_FORM_TYPES.DEFAULT:
      default:
        formatFunction = formatSubmitData
        break
    }
    const dispatchTicketSave = formatFunction(data)

    const headers = { yard: '' }
    if (inOrgMode) {
      const yard = yardDetailsList?.find(item => item.id === dispatchTicketSave?.yardId)
      headers.yard = yard?.name as any
    }

    const updateBody: UpdateDispatchTicketApiArg = {
      yard: headers?.yard,
      updateDispatchTicketBody: dispatchTicketSave,
      dispatchTicketId,
    }
    const createBody: CreateDispatchTicketApiArg = { yard: headers?.yard, createDispatchTicketBody: dispatchTicketSave }

    if (isUpdate) {
      delete (updateBody as any).updateDispatchTicketBody.idempotencyKey
      await updateDispatchTicket(updateBody)
        .unwrap()
        .then(updatedDT => handleSuccess(data, updatedDT))
        .catch(handleError)
    } else {
      await createDispatchTicket(createBody)
        .unwrap()
        .then(newDT => handleSuccess(data, newDT))
        .catch(handleError)
    }
  }

  const onSubmitRecurringDispatchTicket = async (
    dispatchTicketData,
    recurrenceDetails,
    yardId,
    config: DispatchTicketSubmitConfig,
  ) => {
    const { status, handleSuccess, handleError, idempotencyKey } = config

    const defaultEndDate = new Date(dispatchTicketData.scheduleDate)
    defaultEndDate.setMonth(defaultEndDate.getMonth() + 3)

    const formattedDispatchTicket = formatSubmitData({
      ...dispatchTicketData,
      idempotencyKey: idempotencyKey,
    })

    const recurringDispatchTicket = {
      yardId: dispatchTicketData.yardId || yardId,
      scheduleDate: formattedDispatchTicket?.scheduleDate,
      scheduledTimeActive: formattedDispatchTicket?.scheduleTimeActive,
      type: dispatchTicketData.type,
      customerId: dispatchTicketData?.customer?.id,
      contactId: dispatchTicketData?.contactId,
      locationId: dispatchTicketData?.location?.id,
      driverId: dispatchTicketData.driverId ? dispatchTicketData.driverId : undefined,
      includeDriver: recurrenceDetails.includeDriver,
      interYardStartLocationId: formattedDispatchTicket?.interYard?.startLocationId || undefined,
      interYardEndLocationId: formattedDispatchTicket?.interYard?.endLocationId || undefined,
      endDate: recurrenceDetails.endDate || defaultEndDate,
      recurrenceRule: {
        frequency: recurrenceDetails.frequency,
        interval: recurrenceDetails.interval,
        dayOfTheWeek: recurrenceDetails.dayOfTheWeek,
      },
      idempotencyKey: idempotencyKey,
      dispatchTicket: {
        ...formattedDispatchTicket,
        interYard: formattedDispatchTicket?.interYard || undefined,
      },
      note: dispatchTicketData.note || undefined,
    }

    const headers = { yard: '' }
    if (inOrgMode) {
      const yard = yardDetailsList?.find(item => item.id === dispatchTicketData?.yardId)
      headers.yard = yard?.name as any
    }

    const body: CreateRecurringDispatchTicketApiArg = {
      yard: headers?.yard,
      recurringDispatchTicketSchema: recurringDispatchTicket,
    }

    await createRecurringDispatchTicket(body)
      .unwrap()
      .then(updatedDT => handleSuccess(dispatchTicketData, updatedDT))
      .catch(handleError)
  }

  return {
    onSubmitDispatchTicket,
    onSubmitRecurringDispatchTicket,
    dispatchTicketData,
    isLoadingDispatchTicket,
  }
}
